// storageService.js
import {
  connectStorageEmulator,
  deleteObject,
  getDownloadURL,
  getBytes,
  ref,
  listAll,
  uploadBytes
} from "firebase/storage";
import { getFirebaseStorage } from "./firebase"; // Adjust path as needed

// Get storage instance only when needed
const getStorage = () => {
  const storage = getFirebaseStorage();

  // Only connect to emulator if explicitly enabled via env variable
  if (
      storage &&
      process.env.NODE_ENV === 'development' &&
      process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true' &&
      !window._storageEmulatorConnected
  ) {
    try {
      connectStorageEmulator(storage, "localhost", 9199);
      window._storageEmulatorConnected = true;
    } catch (err) {
      console.error("Error connecting to Storage emulator:", err.message);
    }
  }

  return storage;
};

// Refactor your functions to use getStorage() instead of a global storage

export const deleteProviderFiles = async (providerType, providerUsername) => {
  try {
    const storage = getStorage();
    const providerPath = `providers/${providerType}/${providerUsername}`;
    const providerRef = ref(storage, providerPath);
    const filesList = await listAll(providerRef);
    const deletePromises = filesList.items.map((fileRef) => {
      return deleteObject(fileRef);
    });
    await Promise.all(deletePromises);
    return true;
  } catch (error) {
    console.error("Error deleting provider files:", error);
    return false;
  }
};

export const getPhoto = async (photoPath) => {
  if (!photoPath) return null;
  try {
    const storage = getStorage();
    const photoRef = ref(storage, photoPath);
    return await getDownloadURL(photoRef);
  } catch (error) {
    console.error("Error fetching provider photo:", error);
    return null;
  }
};

//A diferencia del getPhoto que devuelve una url de la imagen, aca se obtienen los bytes directamente del archivo
export const getFile = async (filePath) => {
  if (!filePath) return null;

  try {
    const storage = getStorage();
    const fileRef = ref(storage, filePath);

    // Intentar obtener los bytes con retry para problemas de red
    let retries = 3;
    let lastError;

    while (retries > 0) {
      try {
        const bytes = await getBytes(fileRef);

        // Convertir bytes a texto
        const text = new TextDecoder().decode(bytes);

        // Parsear el texto como JSON
        const jsonData = JSON.parse(text);
        return jsonData;
      } catch (error) {
        lastError = error;
        retries--;

        if (retries > 0) {
          console.warn(`Error cargando archivo (${retries} intentos restantes):`, error.message);
          // Esperar un poco antes del siguiente intento
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }

    // Si llegamos aquí, todos los intentos fallaron
    throw lastError;

  } catch (error) {
    console.error("Error fetching file:", error);

    // Manejar diferentes tipos de errores
    if (error.code === 'storage/object-not-found') {
      throw new Error(`El archivo ${filePath} no fue encontrado`);
    } else if (error.message.includes('QUIC') || error.message.includes('network')) {
      throw new Error('Error de conexión con el servidor. Por favor, verifica tu conexión a internet.');
    } else if (error.name === 'SyntaxError') {
      throw new Error('El archivo no contiene un formato JSON válido');
    } else {
      throw new Error(`Error al cargar el archivo: ${error.message}`);
    }
  }
};

export const fetchImages = async (photoPath, workPhotosArray = []) => {
  try {
    const storage = getStorage();
    const imageUrls = {};
    if (photoPath) {
      const profilePhotoRef = ref(storage, photoPath);
      imageUrls.profilePhoto = await getDownloadURL(profilePhotoRef);
    }
    const workPhotosUrls = await Promise.all(
        workPhotosArray.map(async (photo) => {
          try {
            const workPhotoRef = ref(storage, photo);
            return await getDownloadURL(workPhotoRef);
          } catch (error) {
            console.error("Error fetching work photo:", error);
            return null;
          }
        })
    );
    // Filtramos las URLs nulas
    imageUrls.workPhotos = workPhotosUrls.filter((url) => url !== null);
    return imageUrls;
  } catch (error) {
    console.error("Error fetching images:", error);
    throw error;
  }
};

// Subir un JSON con metadata adecuada
export const uploadJson = async (data, path) => {
  try {
    const storage = getStorage();
    const storageRef = ref(storage, path);
    const jsonString = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json; charset=utf-8' });
    const metadata = { contentType: 'application/json; charset=utf-8' };
    await uploadBytes(storageRef, blob, metadata);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error("Error uploading JSON file:", error);
    throw error;
  }
};

export const uploadFile = async (file, path) => {
  try {
    const storage = getStorage();
    const storageRef = ref(storage, path);
    const metadata = { contentType: file.type };
    await uploadBytes(storageRef, file, metadata);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

export const uploadFileWithMetadata = async (file, path) => {
  const storage = getStorage();
  const storageRef = ref(storage, path);
  const metadata = { contentType: file.type };
  await uploadBytes(storageRef, file, metadata);
  return await getDownloadURL(storageRef);
};

export const uploadProfilePhoto = async (file, providerType, providerUsername) => {
  try {
    const path = `providers/${providerType}/${providerUsername}/profile.jpg`;
    return await uploadFile(file, path);
  } catch (error) {
    console.error("Error uploading profile photo:", error);
    throw error;
  }
};

export const uploadWorkPhotos = async (files, providerType, providerUsername) => {
  try {
    const uploadPromises = Array.from(files).map(async (file) => {
      const timestamp = Date.now().toString();
      const path = `providers/${providerType}/${providerUsername}/work_photo_${timestamp}.jpg`;
      const url = await uploadFile(file, path);
      return { workPhotoPath: path, workPhotoUrl: url };
    });
    return await Promise.all(uploadPromises);
  } catch (error) {
    console.error("Error uploading work photos:", error);
    throw error;
  }
};

export const deletePhoto = async (photoPath) => {
  try {
    const storage = getStorage();
    const photoRef = ref(storage, photoPath);
    await deleteObject(photoRef);
    return true;
  } catch (error) {
    console.error("Error deleting photo:", error);
    throw error;
  }
};

export default {
  deleteProviderFiles,
  fetchImages,
  uploadJson,
  uploadFile,
  uploadFileWithMetadata,
  uploadProfilePhoto,
  uploadWorkPhotos,
  deletePhoto,
  getPhoto
};